import React from "react";
import { PillGroup, Pill } from "./ui/Pill";
import { DateRangePills } from "./ui/DateRangePills";
import { STATUS_FILTERS } from "./broadcastEnums";

/**
 * The filter row above the Broadcasts list: status on the left, date range
 * pushed to the right edge.
 *
 * ---------------------------------------------------------------------------
 * WHY THE STATUS CHIPS CARRY NO COUNTS
 * ---------------------------------------------------------------------------
 * The mock shows a number beside each status ("Scheduled 4", "Sent 128").
 * Those are per-view sidebar counts, and the list endpoint does not return
 * them — it returns one page of rows plus a total for the CURRENT filter, not
 * a breakdown across all four. The only ways to put a number there would be to
 * count the loaded page (wrong the moment the list paginates) or to fire three
 * extra list calls purely to read their totals (expensive, and still racy).
 *
 * A wrong count on a filter chip is worse than no count: it is read as a fact
 * about the whole account. So the chips stay countless until the API offers a
 * real breakdown.
 *
 * Purely presentational. Every value arrives as a prop; nothing here fetches.
 */

/**
 * @param {Object} props
 * @param {string} props.status - Selected status filter id, e.g. "all" | "scheduled" | "sent" | "draft".
 * @param {(status: string) => void} [props.onStatusChange] - Called with the newly selected status id.
 * @param {number} props.days - Selected date-range preset, in days.
 * @param {(days: number) => void} [props.onDaysChange] - Called with the NUMERIC day preset.
 * @param {string} [props.className] - Extra classes on the row, merged last.
 * @returns {React.ReactElement}
 */
export const BroadcastFilters = ({
  status,
  onStatusChange,
  days,
  onDaysChange,
  className = "",
}) => (
  <div className={`flex flex-wrap items-center gap-2 ${className}`}>
    <PillGroup label="Status">
      {STATUS_FILTERS.map((filter) => (
        <Pill
          key={filter.id}
          selected={filter.id === status}
          onSelect={() => onStatusChange?.(filter.id)}
        >
          {filter.label}
        </Pill>
      ))}
    </PillGroup>

    {/* `ml-auto` rather than `justify-between`: when the row wraps on a narrow
        viewport the date pills drop to their own line and stay right-aligned,
        instead of the two groups springing apart mid-row. */}
    <DateRangePills days={days} onChange={onDaysChange} className="ml-auto" />
  </div>
);

export default BroadcastFilters;
